import type { StatusCategory } from '../types';

/** t171u: bug reports and feature suggestions from everyone, kept as tasks in the workspace's "Hub feedback" project. */
export type FeedbackKind = 'bug' | 'feature';
export const FEEDBACK_KINDS: { key: FeedbackKind; label: string }[] = [{ key: 'bug', label: 'Bug' }, { key: 'feature', label: 'Feature suggestion' }];
export const FEEDBACK_KIND_LABEL: Record<FeedbackKind, string> = { bug: 'Bug', feature: 'Feature' };

export interface FeedbackInput {
  kind: FeedbackKind; title: string; description?: string; attachment_ids?: string[];
  /** Captured by the app, not typed: the page it was sent from, the browser and the app version. */
  page_url?: string; user_agent?: string; app_version?: string;
}
export interface FeedbackStatus { name: string; category: StatusCategory; color: string }
export interface FeedbackItem {
  id: string; key: string; title: string; kind: FeedbackKind; status: FeedbackStatus;
  created_at: string; updated_at: string; completed_at: string | null; comment_count: number;
}
export interface FeedbackList { items: FeedbackItem[]; /** Set for people with feedback.manage: the board to work them on. */ project_id: string | null }
export interface FeedbackDetail extends FeedbackItem {
  description: string; priority: string; project_id: string; can_manage: boolean;
  reporter: { id: string; name: string }; assignees: { id: string; name: string }[];
  page_url: string | null; app_version: string | null; browser: string | null;
  attachments: { id: string; file_name: string; content_type: string; width: number | null; height: number | null }[];
}

/** "Chrome 130 on macOS", from a user agent; null when it can't tell. */
export function browserLabel(ua: string | null | undefined): string | null {
  if (!ua) return null;
  const pick = (re: RegExp) => ua.match(re)?.[1];
  const browser = pick(/Edg\/(\d+)/) ? `Edge ${pick(/Edg\/(\d+)/)}`
    : pick(/Firefox\/(\d+)/) ? `Firefox ${pick(/Firefox\/(\d+)/)}`
    : pick(/(?:Chrome|CriOS)\/(\d+)/) ? `Chrome ${pick(/(?:Chrome|CriOS)\/(\d+)/)}`
    : pick(/Version\/(\d+(?:\.\d+)?).*Safari/) ? `Safari ${pick(/Version\/(\d+(?:\.\d+)?).*Safari/)}` : null;
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac OS X|Macintosh/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /CrOS/.test(ua) ? 'ChromeOS' : /Linux/.test(ua) ? 'Linux' : null;
  return browser && os ? `${browser} on ${os}` : browser ?? os;
}

/** The captured details, added under the description so they show on the task for whoever picks it up. */
export function feedbackDescription(i: Pick<FeedbackInput, 'description' | 'page_url' | 'user_agent' | 'app_version'>): string {
  const lines = [i.page_url && `Page: ${i.page_url}`, browserLabel(i.user_agent) && `Browser: ${browserLabel(i.user_agent)}`, i.app_version && `App version: ${i.app_version}`].filter(Boolean);
  return [(i.description ?? '').trim(), lines.length ? `---\n${lines.join('\n')}` : ''].filter(Boolean).join('\n\n');
}
